import { currentRegion } from '@/lib/region';
import { ApolloClient, ApolloLink, from, InMemoryCache } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { getAuth } from 'firebase/auth';
import Router from 'next/router';

// GraphQL service of the selected country's region (home: NEXT_PUBLIC_GRAPHQL_URL). No region → no URL,
// so a stray query fails instead of reaching another region's backend.
const uploadLink = createUploadLink({
    uri: currentRegion?.graphqlUrl,
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
        graphQLErrors.forEach(({ extensions }) => {
            if (extensions?.code === 'FORBIDDEN') {
                Router.replace('/auth/signin');
            }
        });
    }

    if (networkError) {
        console.error(`[Network error]: ${networkError}`);
    }
});

let tokenCache = { token: null, timestamp: null };

const getValidToken = async () => {
    const now = Date.now();

    if (tokenCache.token && tokenCache.timestamp && now - tokenCache.timestamp < 5 * 60 * 1000) {
        return tokenCache.token;
    }

    const auth = getAuth();
    const currentUser = auth?.currentUser;
    if (!currentUser) {
        return null;
    }

    try {
        const idToken = await currentUser.getIdToken(true);
        tokenCache = { token: idToken, timestamp: now };
        return idToken;
    } catch (error) {
        console.error('Failed to get ID token for GraphQL:', error);
        return null;
    }
};

const authLink = new ApolloLink(async (operation, forward) => {
    const token = await getValidToken();
    if (token) {
        operation.setContext({
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    }

    return forward(operation);
});

const apolloClient = new ApolloClient({
    link: from([authLink, errorLink, uploadLink]),
    cache: new InMemoryCache({
        typePolicies: {
            // The order-line modifier types expose the *catalog* modifier/group id, which repeats
            // across variant and addon groups. Keep them embedded under their parent instead of
            // normalizing, so per-group unitPrice/amount/quantity are not merged together.
            // Note the casing: these lowercase names are the order types only, the catalog types
            // (Modifier / ModifierGroup) stay normalized.
            modifier: { keyFields: false },
            modifierGroup: { keyFields: false },
        },
    }),
});

export default apolloClient;
